import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { consultingApi, orchestratorApi } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { loadBusinessContext, saveBusinessContext } from '../utils/businessContext';
import {
  normalizeBusinessType,
	resolveStackIdFromBusinessType,
} from '../utils/businessTaxonomy';
import { isReadinessCompletedStored, loadReadinessProfile } from '../utils/readinessStorage';

const recipes = [
	'lead_hunter',
	'content_factory',
	'inbox_shield',
	'compliance_auditor',
	'front_desk_agent',
	'research_oracle',
];

const pretty = (value) => JSON.stringify(value, null, 2);
const toMoney = (value) => {
	const amount = Number(value) || 0;
	return new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'USD',
		maximumFractionDigits: 0,
	}).format(amount);
};

const computeProjectedAnnualSavings = (roi = {}) => {
	const direct = Number(roi?.annual_ai_savings);
	if (Number.isFinite(direct) && direct > 0) return direct;

	const reclaim = Number(roi?.annual_reclaim) || 0;
	const error = Number(roi?.cost_of_error) || 0;
	const combined = reclaim + error;
	if (combined > 0) return combined;

	const manual = Number(roi?.annual_manual_cost) || 0;
	const roiPercent = Number(roi?.roi_percent) || 0;
	if (manual > 0 && roiPercent > 0) {
		return Math.round((manual * roiPercent) / 100);
	}

	return 0;
};

const escapeHtml = (value) => String(value ?? '')
	.replace(/&/g, '&amp;')
	.replace(/</g, '&lt;')
	.replace(/>/g, '&gt;')
	.replace(/"/g, '&quot;')
	.replace(/'/g, '&#039;');

const WORKFLOW_RECIPE_DETAILS = {
	inbox_shield: {
		title: 'Inbox Shield',
		description:
			'Inbox Shield triages incoming messages by urgency, intent, and required owner so high-value requests are never buried. It applies structured routing and draft responses to reduce first-response delays while keeping human approval in the loop for sensitive threads.',
	},
	compliance_auditor: {
		title: 'Compliance Auditor',
		description:
			'Compliance Auditor checks each workflow against your policy and audit constraints before execution continues. It flags risk patterns early and creates a review trail so your team can move faster without sacrificing governance quality.',
	},
	lead_hunter: {
		title: 'Lead Hunter',
		description:
			'Lead Hunter scores and prioritizes opportunities based on intent signals and fit, then sends the best leads to the right follow-up path. This improves speed-to-lead and focuses team effort on opportunities most likely to convert.',
	},
	front_desk_agent: {
		title: 'Front Desk Agent',
		description:
			'Front Desk Agent handles appointment and intake coordination across channels so requests do not depend on manual inbox monitoring. It standardizes booking handoffs and captures complete context before tasks reach operations staff.',
	},
	research_oracle: {
		title: 'Research Oracle',
		description:
			'Research Oracle continuously compiles market, competitor, and policy intelligence into concise operating insights. Teams gain faster decision support and reduce time spent gathering fragmented information from multiple tools.',
	},
	content_factory: {
		title: 'Content Factory',
		description:
			'Content Factory converts reusable business inputs into consistent, channel-ready drafts for campaigns, client updates, and outbound communication. It shortens production cycles while preserving your brand voice and compliance standards.',
	},
};

const formatLabel = (value) => String(value || 'n/a')
	.replace(/_/g, ' ')
	.replace(/\b\w/g, (match) => match.toUpperCase());

const MY_AI_FLOW_DRAFT_KEY = 'wowrolla_my_ai_flow_draft_v1';
const MY_AI_FLOW_PDF_PACKAGE_KEY = 'wowrolla_last_my_ai_flow_pdf_package_v1';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const BUSINESS_TYPE_AI_STACK = {
	legal: {
		stack: [
			{ tool: 'Wowrolla Inbox Shield', role: 'Triage and route client messages by urgency and legal sensitivity', replaces: 'Manual inbox sorting and missed follow-ups' },
			{ tool: 'Wowrolla Compliance Auditor', role: 'Flag regulatory and PIPEDA risks before they surface in client files', replaces: 'Ad-hoc compliance reviews and manual audit checklists' },
			{ tool: 'Wowrolla Front Desk Agent', role: 'Handle intake and appointment booking without manual coordination', replaces: 'Phone-first scheduling and front-staff bottlenecks' },
			{ tool: 'Claude.ai (via Wowrolla)', role: 'Draft bilingual client briefs, intake summaries, and case preparation documents', replaces: 'Hours spent writing repetitive client communications from scratch' },
		],
	},
	accounting: {
		stack: [
			{ tool: 'Wowrolla Inbox Shield', role: 'Prioritize and categorize incoming client requests by filing urgency and type', replaces: 'Manual inbox triage that falls behind during busy periods' },
			{ tool: 'Wowrolla Compliance Auditor', role: 'Auto-detect filing risk flags and regulatory deadline alerts', replaces: 'Manual review checklists that miss edge cases' },
			{ tool: 'Wowrolla Research Oracle', role: 'Monitor fee benchmarks and tax regulation changes relevant to your client base', replaces: 'Weekly manual research and industry reading' },
			{ tool: 'Notion AI + Make.com (via Wowrolla)', role: 'Automate client ledger digests and meeting prep notes', replaces: 'Hours of manual data summarization before appointments' },
		],
	},
	real_estate: {
		stack: [
			{ tool: 'Wowrolla Lead Hunter', role: 'Score and qualify relocation and buyer leads before you even pick up the phone', replaces: 'Manual lead review and time lost on unqualified calls' },
			{ tool: 'Wowrolla Front Desk Agent', role: 'Manage showing bookings and property inquiry routing across channels 24/7', replaces: 'Phone-only booking and after-hours response gaps' },
			{ tool: 'Wowrolla Content Factory', role: 'Produce bilingual listing scripts, social captions, and email sequences', replaces: '2-4 hours per listing on manual content creation' },
			{ tool: 'Claude.ai + Canva (via Wowrolla)', role: 'Generate market updates, client offers, and comparison reports at scale', replaces: 'Multi-tool toggling and inconsistent output quality' },
		],
	},
	dental: {
		stack: [
			{ tool: 'Wowrolla Front Desk Agent', role: 'Confirm appointments, capture patient intake details, and reduce no-shows proactively', replaces: 'Manual confirmation calls and paper intake forms' },
			{ tool: 'Wowrolla Inbox Shield', role: 'Prioritize urgent patient messages and escalate clinical requests immediately', replaces: 'Front-desk bottlenecks and missed after-hours appointment corrections' },
			{ tool: 'Calendly + Claude.ai (via Wowrolla)', role: 'Send recall reminders, procedure follow-ups, and review requests automatically', replaces: 'Manual outreach cadences that break down under team workload' },
		],
	},
	hvac: {
		stack: [
			{ tool: 'Wowrolla Front Desk Agent', role: 'Queue emergency dispatch requests and route technicians by urgency and zone', replaces: 'Phone-only dispatch with manual job assignment' },
			{ tool: 'Wowrolla Inbox Shield', role: 'Triage service requests and separate emergency calls from quote inquiries', replaces: 'Undifferentiated call handling that delays urgent jobs' },
			{ tool: 'Wowrolla Research Oracle', role: 'Track seasonal demand patterns and competitor promotions to sharpen pricing', replaces: 'Manual market monitoring done once a quarter or not at all' },
			{ tool: 'Make.com + HubSpot (via Wowrolla)', role: 'Trigger maintenance contract renewal sequences and follow-up automations', replaces: 'Manual renewal tracking on spreadsheets' },
		],
	},
	plumber: {
		stack: [
			{ tool: 'Wowrolla Front Desk Agent', role: 'Capture job requests from multiple channels and confirm bookings instantly', replaces: 'Phone-first booking with high missed-call rates' },
			{ tool: 'Wowrolla Inbox Shield', role: 'Prioritize emergency plumbing calls and push them to the top of the dispatch queue', replaces: 'Equal-treatment handling that lets urgent jobs wait' },
			{ tool: 'Wowrolla Lead Hunter', role: 'Identify quote-ready leads and trigger follow-up at the right time', replaces: 'Manual follow-up that gets skipped during busy periods' },
			{ tool: 'Make.com + Zapier (via Wowrolla)', role: 'Automate quote follow-up, job confirmation, and review request sequences', replaces: 'Manual outreach that is inconsistent and slow' },
		],
	},
	electrician: {
		stack: [
			{ tool: 'Wowrolla Front Desk Agent', role: 'Capture urgent electrical service requests and book estimates without manual coordination', replaces: 'Phone-only intake that misses after-hours requests' },
			{ tool: 'Wowrolla Compliance Auditor', role: 'Check permit and safety compliance requirements before job deployment', replaces: 'Manual permit tracking that creates delays and rework' },
			{ tool: 'Wowrolla Lead Hunter', role: 'Score and prioritize leads by job value and conversion readiness', replaces: 'Treating all inquiries the same regardless of close potential' },
			{ tool: 'Claude.ai (via Wowrolla)', role: 'Draft bilingual estimate packages, service summaries, and client updates', replaces: 'Manual quote writing per job using no template system' },
		],
	},
	immigration_consultant: {
		stack: [
			{ tool: 'Wowrolla Inbox Shield', role: 'Route incoming case inquiries by urgency, document type, and deadline proximity', replaces: 'Manual inbox monitoring that misses time-sensitive cases' },
			{ tool: 'Wowrolla Compliance Auditor', role: 'Audit document completeness against policy and flag missing requirements early', replaces: 'End-of-process discovery of missing documents that delays submissions' },
			{ tool: 'Wowrolla Content Factory', role: 'Generate bilingual client guidance briefs, policy change bulletins, and case status updates', replaces: 'Manual writing for each client at the cost of consultant time' },
			{ tool: 'Notion AI + Claude.ai (via Wowrolla)', role: 'Create structured case timelines, interview prep documents, and escalation reports', replaces: 'Freeform notes and inconsistent case documentation' },
		],
	},
	mortgage_broker: {
		stack: [
			{ tool: 'Wowrolla Lead Hunter', role: 'Score pre-qualification leads by financial profile and urgency to accelerate pipeline', replaces: 'Manual qualification calls for every lead regardless of readiness' },
			{ tool: 'Wowrolla Inbox Shield', role: 'Prioritize borrower messages, rate-lock urgencies, and document requests by importance', replaces: 'Flat inbox handling that lets hot borrowers cool down' },
			{ tool: 'Wowrolla Research Oracle', role: 'Monitor competitor rate movements and lender promotions daily', replaces: 'Manual rate research that becomes outdated between checks' },
			{ tool: 'HubSpot + Make.com (via Wowrolla)', role: 'Automate document collection reminders and referral partner follow-up sequences', replaces: 'Manual reminders that get skipped under workload pressure' },
		],
	},
	law_enforcement_supplier: {
		stack: [
			{ tool: 'Wowrolla Compliance Auditor', role: 'Audit procurement submissions against agency compliance matrices before submission', replaces: 'Post-submission discovery of compliance gaps that causes resubmission cycles' },
			{ tool: 'Wowrolla Research Oracle', role: 'Track open RFP signals and procurement windows across agency channels', replaces: 'Manual monitoring of government procurement boards' },
			{ tool: 'Wowrolla Inbox Shield', role: 'Route agency and procurement inquiries by priority and required response SLA', replaces: 'Flat email handling that misses agency reply deadlines' },
			{ tool: 'Claude.ai + Notion AI (via Wowrolla)', role: 'Draft compliance responses, certification documentation, and RFP sections faster', replaces: 'Long response prep cycles that compress available turnaround time' },
		],
	},
	_default: {
		stack: [
			{ tool: 'Wowrolla Inbox Shield', role: 'Sort and route all incoming business communication by urgency and owner', replaces: 'Manual inbox triage that breaks down as volume grows' },
			{ tool: 'Wowrolla Front Desk Agent', role: 'Handle appointment and intake coordination across channels 24/7', replaces: 'Manual booking and after-hours response gaps' },
			{ tool: 'Wowrolla Research Oracle', role: 'Surface competitive and market intelligence without manual searching', replaces: 'Time-consuming manual research across fragmented sources' },
			{ tool: 'Claude.ai + Make.com (via Wowrolla)', role: 'Automate document drafting, communication follow-ups, and reporting sequences', replaces: 'Repetitive manual writing and tracking tasks across the team' },
		],
	},
};

const READINESS_WORKFLOW_CONNECTIONS = [
	{
		keywords: ['lead', 'capture', 'qualification', 'routing', 'follow-up'],
		recipes: ['lead_hunter', 'front_desk_agent', 'inbox_shield'],
		improvement:
			'Readiness workflows focused on lead handling map directly to automated qualification, routing, and response workflows in My AI Flow. This reduces lead leakage and improves conversion speed with consistent, policy-aligned follow-up.',
	},
	{
		keywords: ['content', 'listing', 'proposal', 'campaign', 'copy'],
		recipes: ['content_factory', 'research_oracle', 'compliance_auditor'],
		improvement:
			'Content-heavy readiness priorities connect to AI-assisted drafting and review workflows that cut production cycle time. Your team keeps strategic control while repetitive content preparation and validation become automated.',
	},
	{
		keywords: ['compliance', 'audit', 'risk', 'privacy', 'approval'],
		recipes: ['compliance_auditor', 'inbox_shield', 'research_oracle'],
		improvement:
			'Governance-oriented readiness workflows connect to control layers in My AI Flow that enforce required checks before execution. This improves operational velocity without compromising compliance and audit readiness.',
	},
	{
		keywords: ['booking', 'appointment', 'intake', 'support', 'service'],
		recipes: ['front_desk_agent', 'inbox_shield', 'lead_hunter'],
		improvement:
			'Service and booking readiness workflows map to orchestration paths that standardize intake and scheduling handoffs. The result is faster response times, fewer dropped requests, and better customer experience continuity.',
	},
];

const inferToolTransition = (toolName) => {
	const text = String(toolName || '').toLowerCase();
	if (/crm|hubspot|salesforce|pipedrive|zoho/.test(text)) {
		return {
			mode: 'Integrate',
			effectiveness:
				'We connect this system to AI routing and enrichment workflows so your team keeps the existing source of truth while improving speed and data quality.',
		};
	}
	if (/gmail|outlook|email|inbox|calendar|calendly|slack|teams/.test(text)) {
		return {
			mode: 'Assist + Integrate',
			effectiveness:
				'AI assists drafting, triage, and prioritization while integrations push structured actions to the right owner. This reduces response delays and coordination overhead.',
		};
	}
	if (/spreadsheet|excel|sheet|manual|docs|word/.test(text)) {
		return {
			mode: 'Replace Partially',
			effectiveness:
				'Manual steps in this tool are partially replaced with workflow automation, while critical review points stay with your team. This lowers repetitive workload and error rates.',
		};
	}
	if (/legacy chatbot|single-purpose bot|old automation|deprecated/.test(text)) {
		return {
			mode: 'Replace Completely',
			effectiveness:
				'This component is replaced by connected multi-step AI workflows that provide better context handling, approvals, and measurable business outcomes.',
		};
	}
	return {
		mode: 'Assist',
		effectiveness:
			'The current tool is enhanced with AI copilots and orchestration so your team can keep familiar interfaces while reducing repetitive execution time.',
	};
};

const inferReadinessWorkflowConnections = (selectedWorkflows, connectedRecipes, simulationRuns) => {
	const runStatusByRecipe = new Map(
		(simulationRuns || []).map((run) => [run.recipe_key, run.status || 'unknown'])
	);

	return (selectedWorkflows || []).map((workflowName) => {
		const normalized = String(workflowName || '').toLowerCase();
		const matchedRule = READINESS_WORKFLOW_CONNECTIONS.find((rule) =>
			rule.keywords.some((keyword) => normalized.includes(keyword))
		);

		const candidateRecipes = matchedRule?.recipes || connectedRecipes || [];
		const mappedRecipes = candidateRecipes.filter((recipe) => (connectedRecipes || []).includes(recipe));
		const finalRecipes = mappedRecipes.length ? mappedRecipes : candidateRecipes;
		const runSummary = finalRecipes.length
			? finalRecipes
				.map((recipe) => `${formatLabel(recipe)} (${runStatusByRecipe.get(recipe) || 'planned'})`)
				.join(', ')
			: 'No mapped activity yet';

		return {
			workflowName,
			mappedRecipes: finalRecipes,
			runSummary,
			improvement: matchedRule?.improvement ||
				'This readiness priority is connected to an execution plan in My AI Flow so your selected business outcomes are implemented through measurable, trackable automation activity.',
		};
	});
};

const buildPrintableRoiHtml = ({ form, readinessProfile, readinessRoi, profileDetail, simulationRuns }) => {
	const businessProfile = readinessProfile?.business_profile || {};
	const roiSummary = readinessProfile?.roi_summary || readinessRoi || {};
	const selectedWorkflows = Array.isArray(businessProfile.selected_workflows) ? businessProfile.selected_workflows : [];
	const connectedRecipes = Array.isArray(profileDetail?.bundle) ? profileDetail.bundle : [];
	const currentTools = String(businessProfile.current_tool_stack || '')
		.split(',')
		.map((tool) => tool.trim())
		.filter(Boolean);
	const readinessConnections = inferReadinessWorkflowConnections(selectedWorkflows, connectedRecipes, simulationRuns);
	const completedCount = (simulationRuns || []).filter((run) => run.status === 'completed').length;
	const totalCount = (simulationRuns || []).length;

	const businessTypeKey = String(form.businessType || '').toLowerCase();
	const stackDef = BUSINESS_TYPE_AI_STACK[businessTypeKey] || BUSINESS_TYPE_AI_STACK._default;
	const aiStackCards = stackDef.stack.map((item) => `
		<div class="card" style="background: linear-gradient(180deg, #f3fbf5, #eafbef); border-color: #b8e8c8;">
			<h4>${escapeHtml(item.tool)}</h4>
			<p><strong>What it does for you:</strong> ${escapeHtml(item.role)}</p>
			<p class="muted"><em>Replaces:</em> ${escapeHtml(item.replaces)}</p>
		</div>`).join('');

	const connectedWorkflowCards = connectedRecipes.length
		? connectedRecipes.map((recipeKey) => {
			const detail = WORKFLOW_RECIPE_DETAILS[recipeKey] || {
				title: formatLabel(recipeKey),
				description:
					'This connected workflow is configured to remove repetitive manual steps and improve response speed in your operating process. During implementation, we tailor triggers, approvals, and handoffs so this flow directly supports your business goals and measurable ROI.',
			};
			return `
				<div class="card workflow-card">
					<h4>${escapeHtml(detail.title)}</h4>
					<p>${escapeHtml(detail.description)}</p>
					<p class="muted">Recipe key: ${escapeHtml(recipeKey)}</p>
				</div>`;
		}).join('')
		: '<p class="muted">No connected workflows available yet. Run the simulation first to discover and profile connected recipes.</p>';

	const readinessRows = [
		['Business Type', `${formatLabel(form.businessType)}`],
		['Team Size', businessProfile.team_size ?? 'n/a'],
		['Tools in Use', businessProfile.tool_count ?? 'n/a'],
		['Current Tool Stack', businessProfile.current_tool_stack || 'n/a'],
		['Manual Hours / Person / Week', businessProfile.manual_hours_per_person_weekly ?? 'n/a'],
		['Average Hourly Cost', toMoney(businessProfile.hourly_cost)],
		['Data Centralization', formatLabel(businessProfile.data_centralization)],
		['Compliance Load', formatLabel(businessProfile.compliance)],
		['Urgency', formatLabel(businessProfile.urgency)],
		['Change Readiness (1-5)', businessProfile.change_readiness ?? 'n/a'],
	].map(([label, value]) => `
			<div class="metric-row">
				<span>${escapeHtml(label)}</span>
				<strong>${escapeHtml(value)}</strong>
			</div>`).join('');

	const selectedWorkflowList = selectedWorkflows.length
		? selectedWorkflows.map((workflow) => `<li>${escapeHtml(workflow)}</li>`).join('')
		: '<li>No readiness workflows selected</li>';

	const toolTransitionCards = currentTools.length
		? currentTools.map((tool) => {
			const transition = inferToolTransition(tool);
			return `
				<div class="card" style="background: linear-gradient(180deg, #fffdf5, #fff9e8); border-color: #f2dfb6;">
					<h4>${escapeHtml(tool)}</h4>
					<p><strong>AI Treatment:</strong> ${escapeHtml(transition.mode)}</p>
					<p>${escapeHtml(transition.effectiveness)}</p>
				</div>`;
		}).join('')
		: '<p class="muted">No current tool stack listed in Readiness Studio.</p>';

	const readinessConnectionCards = readinessConnections.length
		? readinessConnections.map((item) => `
			<div class="card" style="background: linear-gradient(180deg, #f6fff8, #eefbf1); border-color: #ccebd4;">
				<h4>${escapeHtml(item.workflowName)}</h4>
				<p><strong>Linked action plans and status:</strong> ${escapeHtml(item.runSummary)}</p>
				<p>${escapeHtml(item.improvement)}</p>
			</div>
		`).join('')
		: '<p class="muted">No readiness workflow connections available yet.</p>';

	const simulationRows = (simulationRuns || []).length
		? simulationRuns.map((run) => `
				<tr>
					<td>${escapeHtml(formatLabel(run.recipe_key || 'unknown'))}</td>
					<td>${escapeHtml(run.run_id || 'n/a')}</td>
					<td>${escapeHtml(run.status || 'unknown')}</td>
					<td>${escapeHtml(run.error || '-')}</td>
				</tr>`).join('')
		: '<tr><td colspan="4">No simulation runs recorded yet.</td></tr>';

	return `<!doctype html>
<html>
<head>
	<meta charset="utf-8" />
	<title>My AI Flow ROI Report</title>
	<style>
		body { font-family: "Segoe UI", Arial, sans-serif; margin: 0; color: #1b1f2a; background: #f5f7fb; }
		.page { max-width: 980px; margin: 0 auto; padding: 28px 24px 48px; }
		.hero { background: linear-gradient(120deg, #0f4c81, #0a6aa1); color: #fff; border-radius: 14px; padding: 22px; }
		.hero h1 { margin: 0 0 8px; font-size: 28px; }
		.hero p { margin: 4px 0; opacity: 0.95; }
		.savings-spotlight { margin-top: 18px; border-radius: 14px; padding: 20px; background: linear-gradient(120deg, #0b6b47, #0f8a5f); color: #fff; border: 1px solid #0b6b47; }
		.savings-spotlight .label { font-size: 12px; letter-spacing: 0.08em; text-transform: uppercase; opacity: 0.9; margin-bottom: 6px; }
		.savings-spotlight .value { font-size: 42px; line-height: 1.05; font-weight: 800; margin: 0 0 8px; }
		.savings-spotlight .caption { margin: 0; font-size: 14px; line-height: 1.5; opacity: 0.95; }
		.section { margin-top: 18px; background: #fff; border: 1px solid #dce3ef; border-radius: 12px; padding: 16px 18px; }
		.section h2 { margin: 0 0 12px; font-size: 19px; color: #0f4c81; border-left: 5px solid #2e8bcf; padding-left: 10px; }
		.grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
		.card { border: 1px solid #dce3ef; border-radius: 10px; padding: 12px; background: #fbfdff; }
		.card h3, .card h4 { margin: 0 0 8px; color: #123b5c; }
		.card p { margin: 0 0 6px; line-height: 1.45; }
		.metric-row { display: flex; justify-content: space-between; gap: 12px; padding: 7px 0; border-bottom: 1px dashed #e3e9f2; }
		.metric-row:last-child { border-bottom: none; }
		.workflow-card { background: linear-gradient(180deg, #f8fbff, #f1f6ff); }
		ul { margin: 0; padding-left: 20px; }
		li { margin-bottom: 6px; }
		.muted { color: #5c687f; }
		table { width: 100%; border-collapse: collapse; font-size: 13px; }
		th, td { border: 1px solid #dce3ef; padding: 8px; text-align: left; vertical-align: top; }
		th { background: #edf3ff; color: #1a3c63; }
		.kpi-card { background: linear-gradient(180deg, #f7faff, #eef5ff); border-color: #cfe0ff; }
		.kpi-card.highlight { background: linear-gradient(180deg, #f2fff7, #e8fff1); border-color: #bfe8cf; }
		.kpi-card p { font-size: 28px; font-weight: 800; color: #0c2f54; margin: 0; }
		.kpi-card.highlight p { color: #0f6b47; }
		.kpi-note { margin-top: 10px; font-size: 12px; color: #5c687f; }
		@media print {
			body { background: #fff; }
			.page { padding: 12px; }
			.section, .hero, .card { break-inside: avoid; }
		}
	</style>
</head>
<body>
	<div class="page">
		<section class="hero">
			<h1>Wowrolla My AI Flow ROI Report</h1>
			<p><strong>Client:</strong> ${escapeHtml(form.clientName || 'n/a')}</p>
			<p><strong>Email:</strong> ${escapeHtml(form.contactEmail || 'n/a')}</p>
			<p><strong>Generated:</strong> ${escapeHtml(new Date().toLocaleString())}</p>
		</section>

		<section class="savings-spotlight">
			<div class="label">Potential AI Savings (Annual)</div>
			<p class="value">${escapeHtml(toMoney(roiSummary.annual_ai_savings))}</p>
			<p class="caption">Estimated yearly savings if your top-priority automation plan is rolled out as outlined in this report.</p>
		</section>

		<section class="section">
			<h2>Financial Snapshot at a Glance</h2>
			<div class="grid">
				<div class="card kpi-card"><h3>Current Annual Manual Cost</h3><p>${escapeHtml(toMoney(roiSummary.annual_manual_cost))}</p></div>
				<div class="card kpi-card highlight"><h3>Potential Annual AI Savings</h3><p>${escapeHtml(toMoney(roiSummary.annual_ai_savings))}</p></div>
				<div class="card kpi-card"><h3>Estimated Payback Period</h3><p>${escapeHtml(`${Number(roiSummary.payback_months || 0).toFixed(1)} months`)}</p></div>
			</div>
			<p class="kpi-note">These figures are planning estimates based on your current inputs and are most useful for comparing options and setting rollout priorities.</p>
		</section>

		<section class="section">
			<h2>Your Business Snapshot (From Readiness Studio)</h2>
			${readinessRows}
			<div style="margin-top: 12px;">
				<h3 style="margin: 0 0 8px; color: #123b5c;">Top Priorities You Selected</h3>
				<ul>${selectedWorkflowList}</ul>
			</div>
		</section>

		<section class="section">
			<h2>What We Will Automate First</h2>
			<p class="muted" style="margin-top: 0;">Each automation plan below explains where your team gets faster, more consistent, and easier-to-manage day-to-day operations.</p>
			<div class="grid" style="grid-template-columns: repeat(2, minmax(0, 1fr));">${connectedWorkflowCards}</div>
		</section>

		<section class="section">
			<h2>Recommended AI Toolkit for Your Team</h2>
			<p class="muted" style="margin-top: 0;">Based on your business type, here is the practical toolset we recommend. For each item, we explain what it helps with and which manual work it reduces.</p>
			<div class="grid" style="grid-template-columns: repeat(2, minmax(0, 1fr));">${aiStackCards}</div>
		</section>

		<section class="section">
			<h2>How Your Current Tools Will Be Used</h2>
			<p class="muted" style="margin-top: 0;">Your existing tools are reviewed one by one. We identify whether each tool should be kept and enhanced, connected more effectively, or gradually replaced.</p>
			<div class="grid" style="grid-template-columns: repeat(2, minmax(0, 1fr));">${toolTransitionCards}</div>
		</section>

		<section class="section">
			<h2>How Your Priorities Were Turned Into Action Plans</h2>
			<p class="muted" style="margin-top: 0;">This section shows how each priority from Readiness Studio was translated into a concrete automation action plan, with execution status and expected business impact.</p>
			<div class="grid" style="grid-template-columns: repeat(2, minmax(0, 1fr));">${readinessConnectionCards}</div>
		</section>

		<section class="section">
			<h2>Execution Activity Log</h2>
			<p><strong>Completed Activities:</strong> ${escapeHtml(`${completedCount}/${totalCount}`)}</p>
			<table>
				<thead>
					<tr>
						<th>Action Plan</th>
						<th>Tracking ID</th>
						<th>Execution Status</th>
						<th>Notes</th>
					</tr>
				</thead>
				<tbody>${simulationRows}</tbody>
			</table>
		</section>

		<section class="section" style="border: 1px solid #f5c6cb; background: #fff8f8;">
			<h2 style="color: #721c24;">Legal Disclaimer</h2>
			<p style="font-size: 12px; line-height: 1.6; color: #555;">
				<strong>FOR ILLUSTRATIVE AND PLANNING PURPOSES ONLY &mdash; NOT FINANCIAL ADVICE.</strong> The financial projections, return-on-investment estimates, payback period calculations, cost savings forecasts, and operational metrics contained in this report are generated using generalized modeling assumptions derived solely from user-submitted inputs. These figures are illustrative estimates intended to support preliminary business planning discussions only and do not constitute financial advice, professional recommendations, or guarantees of any specific outcome.
			</p>
			<p style="font-size: 12px; line-height: 1.6; color: #555; margin-top: 8px;">
				THE ACTUAL RESULTS EXPERIENCED BY ANY BUSINESS WILL VARY MATERIALLY based on, but not limited to: specific operational conditions, existing technology infrastructure, staff adoption rates, implementation timeline, existing vendor agreements, regulatory and compliance requirements, market conditions, data quality, change management capacity, and other factors that cannot be accurately anticipated or quantified at this stage.
			</p>
			<p style="font-size: 12px; line-height: 1.6; color: #555; margin-top: 8px;">
				Wowrolla LLC, its officers, directors, employees, agents, and affiliates hereby disclaim all representations and warranties, whether express or implied, regarding the accuracy, completeness, reliability, or fitness for a particular purpose of the estimates and projections contained herein. Nothing in this report constitutes a binding commitment, a guarantee of savings or returns, or a formal business proposal. Reliance upon the information in this report for any financial, investment, operational, or legal decision is at the recipient's sole risk. You should consult with qualified financial, legal, and technical professionals before making any such decisions.
			</p>
		</section>

		<section class="section" style="border: 1px solid #cce3f5; background: #f5faff; margin-top: 10px;">
			<h2 style="color: #0f4c81; font-size: 14px; margin-bottom: 10px;">Canadian Legal &amp; Privacy Disclosures (Fine Print)</h2>
			<p style="font-size: 11px; line-height: 1.6; color: #444; margin-bottom: 6px;">
				<strong>AFFILIATE DISCLOSURE &mdash; Competition Bureau of Canada:</strong> Wowrolla earns affiliate commissions on some AI tool recommendations and links. This constitutes a material connection between Wowrolla and those tool providers, disclosed in accordance with the <em>Competition Act</em> (Canada) and the Competition Bureau&rsquo;s guidelines on endorsements and testimonials. Commission arrangements do not influence methodology, rankings, or the recommendations in this report.
			</p>
			<p style="font-size: 11px; line-height: 1.6; color: #444; margin-bottom: 6px;">
				<strong>PRIVACY (PIPEDA):</strong> Personal information collected in connection with this engagement is governed by the <em>Personal Information Protection and Electronic Documents Act</em> (PIPEDA, S.C. 2000, c. 5). Information is collected with consent, used only for the purposes identified at collection, and retained only as long as necessary. You have the right to access and correct your personal information by contacting support@wowrolla.ca or the Office of the Privacy Commissioner of Canada at priv.gc.ca.
			</p>
			<p style="font-size: 11px; line-height: 1.6; color: #444; margin-bottom: 6px;">
				<strong>QUEBEC RESIDENTS (Law 25 / Loi 25):</strong> If you are a Quebec resident, you have additional rights under Quebec&rsquo;s <em>Act Respecting the Protection of Personal Information in the Private Sector</em> (Law 25, in force September 2023), including rights to data portability and erasure. Contact support@wowrolla.ca or the Commission d&rsquo;acc&egrave;s &agrave; l&rsquo;information du Qu&eacute;bec at cai.quebec.ca.
			</p>
			<p style="font-size: 11px; line-height: 1.6; color: #444; margin-bottom: 6px;">
				<strong>DATA HOSTING (CLOUD Act):</strong> Data related to this engagement is stored on servers located in Canada, operated by a third-party cloud infrastructure provider. Our hosting provider may be incorporated in the US, and US law enforcement may have legal access to data under the US CLOUD Act regardless of server location. Wowrolla does not voluntarily share data with any government authority unless compelled by law.
			</p>
			<p style="font-size: 11px; line-height: 1.6; color: #444; margin-bottom: 6px;">
				<strong>ANTI-SPAM (CASL):</strong> Any marketing or follow-up email communications from Wowrolla require your express consent. We use double opt-in for all mailing list subscriptions in compliance with Canada&rsquo;s Anti-Spam Legislation (CASL, S.C. 2010, c. 23). You may withdraw consent and unsubscribe at any time.
			</p>
			<p style="font-size: 11px; line-height: 1.6; color: #444; margin-bottom: 6px;">
				<strong>GOVERNING LAW:</strong> This document and any related engagement are governed by the laws of Ontario and applicable federal laws of Canada. Disputes are subject to the exclusive jurisdiction of the courts of Ottawa, Ontario, unless otherwise agreed in writing.
			</p>
			<p style="font-size: 10px; line-height: 1.5; color: #888; margin-top: 8px; border-top: 1px solid #d8e8f5; padding-top: 6px;">
				Wowrolla &bull; Ottawa, Ontario, Canada &bull; support@wowrolla.ca &bull; priv.gc.ca (OPC) &bull; cai.quebec.ca (CAI) &bull; Effective April 29, 2026
			</p>
			<p style="font-size: 11px; line-height: 1.5; color: #888; margin-top: 8px;">
				Report generated: ${escapeHtml(new Date().toLocaleString())} &nbsp;|&nbsp; This document is confidential and intended solely for the named recipient. Unauthorized distribution is prohibited.
			</p>
		</section>
	</div>
</body>
</html>`;
};

const OrchestratorLabPage = () => {
	const { user, isAuthenticated } = useAuth();
	const [form, setForm] = useState({
		tenantId: '',
		clientId: '',
		clientName: '',
		businessType: '',
		region: 'Ottawa, Canada',
		contactEmail: '',
		recipeKey: 'lead_hunter',
		sopName: 'My AI Flow SOP',
		objective: 'Run selected workflows from Readiness Studio and generate ROI outcomes.',
		runPayload: '{\n  "lead_name": "Alex Mercier",\n  "message": "Bonjour, je suis mute a Ottawa pour un poste federal.",\n  "preferred_neighborhood": "Kanata",\n  "email": "alex.mercier@gc.ca",\n  "phone": "+1 613-555-0199"\n}',
		approvedBy: 'qa.tester@wowrolla.local',
		runId: '',
	});

	const [state, setState] = useState({
		loading: false,
		health: null,
		latest: null,
		logs: null,
		businessTypes: [],
		profileDetail: null,
		salesKit: null,
		simulationRuns: [],
		readinessProfile: null,
		lastHealthCheckedAt: null,
		lastLogsRefreshedAt: null,
		pdfLoading: false,
		hydratedFromLogin: false,
		journeyStageKeys: [],
		aiRuntimeLabel: null,
		error: '',
	});

	const lastLog = useMemo(() => state.logs?.logs?.[0] || null, [state.logs]);
	const readinessComplete = useMemo(() => {
		if (typeof window === 'undefined') return false;
		return isReadinessCompletedStored(isAuthenticated);
	}, [isAuthenticated]);

	const readinessRoi = useMemo(() => state.readinessProfile?.roi_summary || {}, [state.readinessProfile]);
	const projectedAnnualSavings = useMemo(
		() => computeProjectedAnnualSavings(readinessRoi),
		[readinessRoi],
	);
	const recipeOptions = useMemo(() => {
		if (Array.isArray(state.profileDetail?.bundle) && state.profileDetail.bundle.length > 0) {
			return state.profileDetail.bundle;
		}
		return recipes;
	}, [state.profileDetail]);

	const isValidContactEmail = useMemo(
		() => EMAIL_REGEX.test(String(form.contactEmail || '').trim()),
		[form.contactEmail],
	);
	const canLoadBusinessTypeDetail = Boolean(form.businessType);
	const canSetupClient = Boolean(form.tenantId && form.clientId && isValidContactEmail);
	const canBootstrap = canSetupClient;
	const canCreateRun = Boolean(form.tenantId && form.clientId && form.recipeKey);
	const canApproveRun = Boolean(form.runId?.trim());
	const canExecuteRun = Boolean(form.runId?.trim());
	const canRefreshLogs = Boolean(form.tenantId && form.clientId);
	const canRunFullWorkflow = Boolean(form.tenantId && form.clientId && form.businessType && isValidContactEmail);
	const canDownloadPdf = Boolean(
		!state.pdfLoading && (state.salesKit || (state.simulationRuns?.length || 0) > 0),
	);

	const nextActionMessage = useMemo(() => {
		if (!state.health) {
			return 'Start by checking orchestrator health to verify the API is available.';
		}
		if (!state.businessTypes.length) {
			return 'Load available business types so you can select the correct business profile.';
		}
		if (!form.businessType) {
			return 'Choose a business type before loading the profile details.';
		}
		if (!state.profileDetail?.business_type) {
			return 'Load the selected business type details to unlock recipe and workflow insights.';
		}
		if (!state.salesKit) {
			return 'Bootstrap the client by business type to prepare My AI Flow for simulation.';
		}
		if (!form.runId?.trim()) {
			return 'Create a run, approve it, and then execute the workflow.';
		}
		return 'Your run is ready. Execute it or download the ROI PDF once complete.';
	}, [state.health, state.businessTypes.length, state.profileDetail, state.salesKit, form.businessType, form.runId]);

	const updateField = (key, value) => {
		setForm((prev) => ({ ...prev, [key]: value }));
	};

	const hasReadinessContext = useCallback(() => {
		const parsed = loadReadinessProfile(isAuthenticated);
		return Boolean(parsed?.completed && parsed?.business_profile?.business_type);
	}, [isAuthenticated]);

	useEffect(() => {
		const context = loadBusinessContext(isAuthenticated);
		if (!context?.businessType && !context?.selectedStackId) return;
		setForm((prev) => ({
			...prev,
			businessType: normalizeBusinessType(context.businessType || prev.businessType, ''),
		}));
	}, [isAuthenticated]);

	const hydrateFromClientLogin = useCallback(() => {
		if (!isAuthenticated || !user) {
			return;
		}

		const inferredBusinessType = (user.role_profile || '').toLowerCase().includes('account')
			? 'accounting'
			: (user.role_profile || '').toLowerCase().includes('plumb')
				? 'plumber'
				: (user.role_profile || '').toLowerCase().includes('hvac')
					? 'hvac'
					: (user.role_profile || '').toLowerCase().includes('dent')
						? 'dental'
						: (user.role_profile || '').toLowerCase().includes('immigration')
							? 'immigration_consultant'
							: (user.role_profile || '').toLowerCase().includes('mortgage')
								? 'mortgage_broker'
								: (user.role_profile || '').toLowerCase().includes('enforcement') || (user.role_profile || '').toLowerCase().includes('procurement')
									? 'law_enforcement_supplier'
									: (user.role_profile || '').toLowerCase().includes('electric')
										? 'electrician'
										: (user.role_profile || '').toLowerCase().includes('real')
											? 'real_estate'
											: 'legal';

		setForm((prev) => ({
			...prev,
			tenantId: `tenant-${user.id || 'client'}`,
			clientId: user.id || prev.clientId,
			clientName: user.full_name || prev.clientName,
			contactEmail: user.email || prev.contactEmail,
			businessType: prev.businessType || (hasReadinessContext() ? prev.businessType : inferredBusinessType),
		}));
		setState((prev) => ({ ...prev, hydratedFromLogin: true }));
	}, [hasReadinessContext, isAuthenticated, user]);

	useEffect(() => {
		if (!state.hydratedFromLogin && isAuthenticated && user) {
			hydrateFromClientLogin();
		}
	}, [isAuthenticated, user, state.hydratedFromLogin, hydrateFromClientLogin]);

	const hydrateReadinessSnapshot = useCallback(() => {
		if (typeof window === 'undefined') return;

		try {
			const parsed = loadReadinessProfile(isAuthenticated);
			if (!parsed) return;

			const profile = parsed?.business_profile || {};
			const roiSummary = parsed?.roi_summary || {};
			const normalizedRoiSummary = {
				...roiSummary,
				annual_ai_savings: computeProjectedAnnualSavings(roiSummary),
			};
			const inferredBusinessType = normalizeBusinessType(profile.business_type || '', '');
			const selected = Array.isArray(profile.selected_workflows) ? profile.selected_workflows : [];

			setForm((prev) => ({
				...prev,
				businessType: normalizeBusinessType(inferredBusinessType, prev.businessType || ''),
				objective: selected.length
					? `Execute prioritized workflows from Readiness Studio: ${selected.join(', ')}.`
					: prev.objective,
				sopName: selected.length
					? `My AI Flow - ${selected[0]} SOP`
					: prev.sopName,
				runPayload: JSON.stringify(
					{
						readiness_source: 'readiness_studio',
						selected_workflows: selected,
						team_size: Number(profile.team_size) || 0,
						manual_hours_per_person_weekly: Number(profile.manual_hours_per_person_weekly) || 0,
						hourly_cost: Number(profile.hourly_cost) || 0,
						current_tool_stack: profile.current_tool_stack || '',
						tool_count: Number(profile.tool_count) || 0,
						data_centralization: profile.data_centralization || 'partial',
						compliance: profile.compliance || 'medium',
						urgency: profile.urgency || 'quarter',
						projected_annual_savings: Number(normalizedRoiSummary.annual_ai_savings) || 0,
						projected_payback_months: Number(normalizedRoiSummary.payback_months) || 0,
					},
					null,
					2
				),
			}));

			setState((prev) => ({
				...prev,
				readinessProfile: {
					...parsed,
					roi_summary: normalizedRoiSummary,
				},
			}));
		} catch {
			// Ignore malformed local readiness cache.
		}
	}, [isAuthenticated]);

	useEffect(() => {
		hydrateReadinessSnapshot();
	}, [hydrateReadinessSnapshot]);

	useEffect(() => {
		if (typeof window === 'undefined') return undefined;

		const handleRefresh = () => hydrateReadinessSnapshot();
		window.addEventListener('storage', handleRefresh);
		window.addEventListener('focus', handleRefresh);
		window.addEventListener('pageshow', handleRefresh);

		return () => {
			window.removeEventListener('storage', handleRefresh);
			window.removeEventListener('focus', handleRefresh);
			window.removeEventListener('pageshow', handleRefresh);
		};
	}, [hydrateReadinessSnapshot]);

	useEffect(() => {
		if (typeof window === 'undefined') return;
		try {
			const stored = JSON.parse(window.sessionStorage.getItem(MY_AI_FLOW_DRAFT_KEY) || '{}');
			if (!stored || typeof stored !== 'object') return;
			const normalizedBusinessType = normalizeBusinessType(stored.businessType || '', '');
			setForm((prev) => ({
				...prev,
				clientName: stored.clientName || prev.clientName,
				contactEmail: stored.contactEmail || prev.contactEmail,
				businessType: normalizedBusinessType || prev.businessType,
			}));
		} catch {
			// ignore malformed draft
		}
	}, []);

	useEffect(() => {
		if (typeof window === 'undefined') return;
		try {
			window.sessionStorage.setItem(MY_AI_FLOW_DRAFT_KEY, JSON.stringify({
				clientName: form.clientName,
				contactEmail: form.contactEmail,
				businessType: form.businessType,
				updatedAt: new Date().toISOString(),
			}));
		} catch {
			// ignore storage errors
		}
	}, [form.clientName, form.contactEmail, form.businessType]);

	useEffect(() => {
		saveBusinessContext({
			businessType: form.businessType,
			selectedStackId: resolveStackIdFromBusinessType(form.businessType, ''),
			sourceScreen: 'my-ai-flow',
		}, isAuthenticated);
	}, [form.businessType, isAuthenticated]);

	useEffect(() => {
		orchestratorApi.getBusinessTypes()
			.then((result) => {
				const types = Array.isArray(result?.types) ? result.types : [];
				const supportedKeys = new Set(types.map((item) => item.business_type));
				setState((prev) => ({ ...prev, businessTypes: types }));

				setForm((prev) => {
					if (!types.length) return prev;
					if (supportedKeys.has(prev.businessType)) return prev;

					const normalized = normalizeBusinessType(prev.businessType, '');
					const nextBusinessType = supportedKeys.has(normalized)
						? normalized
						: (types[0]?.business_type || '');

					return {
						...prev,
						businessType: nextBusinessType,
					};
				});
			})
			.catch(() => {
				// Keep flow working with static mapping fallback.
			});
	}, []);

	// Load journey stages once tenantId + clientId are available.
	useEffect(() => {
		if (form.tenantId && form.clientId) {
			loadJourneyStages(form.tenantId, form.clientId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [form.tenantId, form.clientId]);

	useEffect(() => {
		consultingApi.getAiRuntimeLabel()
			.then((res) => {
				setState((prev) => ({ ...prev, aiRuntimeLabel: res?.data || null }));
			})
			.catch(() => {
				// Keep simulation flow working even if label metadata fails to load.
			});
	}, []);

	const parseRunPayload = () => {
		try {
			return JSON.parse(form.runPayload);
		} catch (error) {
			throw new Error(`Invalid run payload JSON: ${error.message}`);
		}
	};

	const parseRunPayloadSafely = () => {
		try {
			return JSON.parse(form.runPayload);
		} catch {
			return {};
		}
	};

	const runAction = async (fn) => {
		setState((prev) => ({ ...prev, loading: true, error: '' }));
		try {
			const result = await fn();
			setState((prev) => ({ ...prev, latest: result, loading: false }));
			return result;
		} catch (error) {
			const message = error?.response?.data?.detail || error?.message || 'Request failed';
			setState((prev) => ({ ...prev, loading: false, error: message }));
			return null;
		}
	};

	const checkHealth = () => runAction(async () => {
		const health = await orchestratorApi.health();
		setState((prev) => ({ ...prev, health, lastHealthCheckedAt: new Date().toISOString() }));
		return health;
	});

	const loadBusinessTypes = () => runAction(async () => {
		const result = await orchestratorApi.getBusinessTypes();
		const types = result?.types || [];
		setState((prev) => ({ ...prev, businessTypes: types }));
		if (types.length > 0 && !types.some((t) => t.business_type === form.businessType)) {
			updateField('businessType', types[0].business_type);
		}
		return result;
	});

	const loadBusinessTypeDetail = () => runAction(async () => {
		const detail = await orchestratorApi.getBusinessTypeDetail(form.businessType);
		setState((prev) => ({ ...prev, profileDetail: detail }));

		if (Array.isArray(detail?.bundle) && detail.bundle.length > 0) {
			updateField('recipeKey', detail.bundle[0]);
		}

		return detail;
	});

	const setupClientAndMission = () => runAction(async () => {
		// Basic validation: ensure contact email looks valid before creating client/mission
		if (!EMAIL_REGEX.test(String(form.contactEmail || '').trim())) {
			throw new Error('Provide a valid contact email before setting up the client');
		}
		// Send the canonical business_type through the orchestrator bootstrap path so My AI Flow shares the same profile contract.
		const clientRes = await orchestratorApi.createClient({
			tenant_id: form.tenantId,
			client_name: form.clientName,
			business_type: form.businessType,
			region: form.region,
			contact_email: form.contactEmail,
		});

		const missionRes = await orchestratorApi.upsertMissionProfile({
			tenant_id: form.tenantId,
			client_id: form.clientId,
			recipe_key: form.recipeKey,
			sop_name: form.sopName,
			objective: form.objective,
			fallback_tools: ['firecrawl', 'apollo', 'serper'],
			checklist: ['PII scrubbed', 'human approval required', 'log reasoning steps'],
			is_active: true,
		});

		return { clientRes, missionRes };
	});

	const bootstrapClientByType = () => runAction(async () => {
		if (!EMAIL_REGEX.test(String(form.contactEmail || '').trim())) {
			throw new Error('Provide a valid contact email before bootstrapping the client');
		}
		const response = await orchestratorApi.bootstrapClient({
			tenant_id: form.tenantId,
			client_id: form.clientId,
			client_name: form.clientName,
			business_type: form.businessType,
			region: form.region,
			contact_email: form.contactEmail,
		});

		setState((prev) => ({ ...prev, salesKit: response.sales_kit, profileDetail: {
			business_type: response.sales_kit.business_type,
			label: response.sales_kit.label,
			bundle: (response.mission_profiles || []).map((item) => item.recipe_key),
			workflow_summary: response.sales_kit.workflow_summary,
			documents: response.sales_kit.documents,
			pilot_stats: response.sales_kit.pilot_stats,
		} }));

		const firstRecipe = response?.mission_profiles?.[0]?.recipe_key;
		if (firstRecipe) {
			updateField('recipeKey', firstRecipe);
		}

		return response;
	});

	const createRun = () => runAction(async () => {
		const runPayload = parseRunPayload();
		const runRes = await orchestratorApi.createRun({
			tenant_id: form.tenantId,
			client_id: form.clientId,
			recipe_key: form.recipeKey,
			input_payload: runPayload,
		});

		if (runRes?.run_id) {
			updateField('runId', runRes.run_id);
		}

		return runRes;
	});

	const approveRun = () => runAction(async () => {
		if (!form.runId) throw new Error('Run ID is required to approve.');
		return orchestratorApi.setApproval(form.runId, {
			tenant_id: form.tenantId,
			is_approved: true,
			approved_by: form.approvedBy,
		});
	});

	const executeRun = () => runAction(async () => {
		if (!form.runId) throw new Error('Run ID is required to execute.');
		return orchestratorApi.executeRun(form.runId, { tenant_id: form.tenantId });
	});

	const refreshLogs = () => runAction(async () => {
		const logs = await orchestratorApi.getLogs(form.tenantId, 5);
		setState((prev) => ({ ...prev, logs, lastLogsRefreshedAt: new Date().toISOString() }));
		return logs;
	});

	const loadJourneyStages = async (tenantId, clientId) => {
		if (!tenantId || !clientId) return;
		try {
			const result = await orchestratorApi.getJourneyStages(tenantId, clientId);
			setState((prev) => ({ ...prev, journeyStageKeys: result?.completed_stage_keys || [] }));
		} catch {
			// Keep UI responsive when orchestrator is unavailable.
		}
	};

	const runFullWorkflow = () => runAction(async () => {
		if (!EMAIL_REGEX.test(String(form.contactEmail || '').trim())) {
			throw new Error('Provide a valid contact email before running the workflow');
		}
		const health = await orchestratorApi.health();
		const clientRes = await orchestratorApi.createClient({
			tenant_id: form.tenantId,
			client_name: form.clientName,
			business_type: form.businessType,
			region: form.region,
			contact_email: form.contactEmail,
		});
		const bootstrapRes = await orchestratorApi.bootstrapClient({
			tenant_id: form.tenantId,
			client_id: form.clientId,
			client_name: form.clientName,
			business_type: form.businessType,
			region: form.region,
			contact_email: form.contactEmail,
		});

		const connectedRecipes = (bootstrapRes?.mission_profiles || []).map((item) => item.recipe_key).filter(Boolean);
		const recipesToRun = connectedRecipes.length ? connectedRecipes : [form.recipeKey];
		updateField('recipeKey', recipesToRun[0]);

		// Record simulation start in journey.
		orchestratorApi.upsertJourneyStage({
			tenant_id: form.tenantId,
			client_id: form.clientId,
			stage: 'simulation_started',
			metadata: { recipe_count: recipesToRun.length, business_type: form.businessType },
		}).catch(() => {});

		const runPayload = parseRunPayload();
		const simulationRuns = [];
		let lastRunRes = null;
		let lastApprovalRes = null;
		let lastExecuteRes = null;

		for (const recipeKey of recipesToRun) {
			try {
				const runRes = await orchestratorApi.createRun({
					tenant_id: form.tenantId,
					client_id: form.clientId,
					recipe_key: recipeKey,
					input_payload: runPayload,
				});

				const nextRunId = runRes?.run_id || '';
				if (nextRunId) {
					updateField('runId', nextRunId);
				}

				const approvalRes = await orchestratorApi.setApproval(nextRunId, {
					tenant_id: form.tenantId,
					is_approved: true,
					approved_by: form.approvedBy,
				});
				const executeRes = await orchestratorApi.executeRun(nextRunId, { tenant_id: form.tenantId });

				simulationRuns.push({
					recipe_key: recipeKey,
					run_id: nextRunId,
					status: 'completed',
					run: runRes,
					approval: approvalRes,
					execution: executeRes,
				});

				lastRunRes = runRes;
				lastApprovalRes = approvalRes;
				lastExecuteRes = executeRes;
			} catch (error) {
				simulationRuns.push({
					recipe_key: recipeKey,
					status: 'failed',
					error: error?.response?.data?.detail || error?.message || 'Execution failed',
				});
			}
		}

		const logs = await orchestratorApi.getLogs(form.tenantId, 5);

		setState((prev) => ({
			...prev,
			health,
			logs,
			salesKit: bootstrapRes.sales_kit,
			simulationRuns,
			profileDetail: {
				business_type: bootstrapRes.sales_kit.business_type,
				label: bootstrapRes.sales_kit.label,
				bundle: recipesToRun,
				workflow_summary: bootstrapRes.sales_kit.workflow_summary,
				documents: bootstrapRes.sales_kit.documents,
				pilot_stats: bootstrapRes.sales_kit.pilot_stats,
			},
		}));

		// Record pilot outcome in journey.
		const completedRunCount = simulationRuns.filter((r) => r.status === 'completed').length;
		orchestratorApi.upsertJourneyStage({
			tenant_id: form.tenantId,
			client_id: form.clientId,
			stage: 'pilot_completed',
			metadata: {
				completed_runs: completedRunCount,
				total_runs: simulationRuns.length,
				business_type: form.businessType,
				annual_ai_savings: projectedAnnualSavings,
			},
		}).then(() => loadJourneyStages(form.tenantId, form.clientId)).catch(() => {});

		// Persist session stats to user profile (best-effort, authenticated only)
		if (isAuthenticated) {
			consultingApi.saveSessionStats({
				business_type: form.businessType || '',
				simulation_runs: simulationRuns,
				completed_count: completedRunCount,
				total_count: simulationRuns.length,
				pdf_generated: false,
				annual_ai_savings: projectedAnnualSavings,
				payback_months: Number(readinessRoi.payback_months) || 0,
			}).catch(() => {});
		}

		return {
			health,
			clientRes,
			bootstrapRes,
			runRes: lastRunRes,
			approvalRes: lastApprovalRes,
			executeRes: lastExecuteRes,
			simulationRuns,
		};
	});

	const downloadMyAiFlowPdf = async () => {
		setState((prev) => ({ ...prev, pdfLoading: true, error: '' }));
		try {
			const latestReadinessProfile = loadReadinessProfile(isAuthenticated) || state.readinessProfile || {};
			const readinessBusinessProfile = latestReadinessProfile?.business_profile || {};
			const readinessRoiSummary = latestReadinessProfile?.roi_summary || state.readinessProfile?.roi_summary || {};
			const runPayloadSnapshot = parseRunPayloadSafely();

			const runPayloadSelectedWorkflows = Array.isArray(runPayloadSnapshot?.selected_workflows)
				? runPayloadSnapshot.selected_workflows.filter(Boolean)
				: [];
			const readinessSelectedWorkflows = Array.isArray(readinessBusinessProfile?.selected_workflows)
				? readinessBusinessProfile.selected_workflows.filter(Boolean)
				: [];
			const profileDetailBundle = Array.isArray(state.profileDetail?.bundle)
				? state.profileDetail.bundle.filter(Boolean)
				: [];
			const workflowBundle = Array.from(new Set([
				...profileDetailBundle,
				...runPayloadSelectedWorkflows,
				...readinessSelectedWorkflows,
			]));

			const teamSize = Number(runPayloadSnapshot.team_size || readinessBusinessProfile.team_size) || 0;
			const manualHoursPerPersonWeekly = Number(runPayloadSnapshot.manual_hours_per_person_weekly || readinessBusinessProfile.manual_hours_per_person_weekly) || 0;
			const hourlyCost = Number(runPayloadSnapshot.hourly_cost || readinessBusinessProfile.hourly_cost) || 0;
			const computedAnnualManualBaseline = Math.round(teamSize * manualHoursPerPersonWeekly * hourlyCost * 52);
			const annualManualBaseline = Number(
				runPayloadSnapshot.annual_manual_cost
				|| readinessRoiSummary.annual_manual_cost
				|| readinessRoi.annual_manual_cost
				|| computedAnnualManualBaseline
				|| readinessRoi.annual_reclaim
			) || 0;

			const annualAiSavings = Number(
				runPayloadSnapshot.projected_annual_savings
				|| readinessRoiSummary.annual_ai_savings
				|| readinessRoi.annual_ai_savings
				|| ((Number(readinessRoiSummary.annual_reclaim) || Number(readinessRoi.annual_reclaim) || 0)
					+ (Number(readinessRoiSummary.cost_of_error) || Number(readinessRoi.cost_of_error) || 0))
			) || 0;

			const paybackMonths = Number(
				runPayloadSnapshot.projected_payback_months
				|| readinessRoiSummary.payback_months
				|| readinessRoi.payback_months
			) || 0;

			const twelveMonthNet = Number(
				readinessRoiSummary.twelve_month_net
				|| readinessRoi.twelve_month_net
				|| readinessRoiSummary.net_3yr_roi
				|| readinessRoi.net_3yr_roi
				|| annualAiSavings
			) || 0;

			const roiPercent = Number(
				readinessRoiSummary.roi_percent
				|| readinessRoi.roi_percent
				|| ((annualAiSavings / Math.max(annualManualBaseline || 1, 1)) * 100)
			) || 0;

			const effectiveBusinessType = form.businessType
				|| normalizeBusinessType(readinessBusinessProfile.business_type || '', '');
			const recommendedStack = (BUSINESS_TYPE_AI_STACK[effectiveBusinessType] || BUSINESS_TYPE_AI_STACK._default || {}).stack || [];
			const workflowContext = workflowBundle.map((recipeKey) => {
				const details = WORKFLOW_RECIPE_DETAILS[recipeKey] || null;
				return {
					recipe_key: recipeKey,
					title: details?.title || formatLabel(recipeKey),
					description: details?.description || '',
				};
			});

			const payload = {
				payload_version: 'my-ai-flow-pdf-v2',
				generated_at: new Date().toISOString(),
				client_name: form.clientName,
				contact_email: form.contactEmail,
				business_type: effectiveBusinessType || form.businessType || '',
				workflow_bundle: workflowBundle,
				workflow_context: workflowContext,
				sales_kit: state.salesKit || {},
				recommended_ai_stack: recommendedStack,
				current_tool_stack: readinessBusinessProfile.current_tool_stack || '',
				selected_workflows: workflowBundle,
				simulation_input_snapshot: runPayloadSnapshot,
				readiness_snapshot: {
					business_profile: readinessBusinessProfile,
					roi_summary: readinessRoiSummary,
				},
				roi: {
					annual_manual_cost: annualManualBaseline,
					annual_ai_savings: annualAiSavings,
					annual_reclaim: Number(readinessRoi.annual_reclaim) || 0,
					cost_of_error: Number(readinessRoi.cost_of_error) || 0,
					monthly_inaction: Number(readinessRoi.monthly_inaction) || 0,
					pilot_fee: Number(readinessRoi.pilot_fee) || 0,
					build_fee: Number(readinessRoi.build_fee) || 0,
					net_3yr_roi: Number(readinessRoi.net_3yr_roi) || 0,
					payback_months: paybackMonths,
					roi_percent: Number(roiPercent.toFixed(1)),
					twelve_month_net: twelveMonthNet,
					monthly_projection: Array.from({ length: 12 }).map((_, idx) => ({
						month: idx + 1,
						estimated_savings: annualAiSavings / 12,
						cumulative_net: (annualAiSavings / 12) * (idx + 1),
					})),
				},
				simulation_runs: state.simulationRuns || [],
			};

			if (typeof window !== 'undefined') {
				try {
					window.sessionStorage.setItem(MY_AI_FLOW_PDF_PACKAGE_KEY, JSON.stringify(payload));
				} catch {
					// ignore storage errors
				}
			}

			const response = await consultingApi.downloadMyAiFlowPdf(payload);
			const rawData = response?.data;
			const blob = rawData instanceof Blob ? rawData : new Blob([rawData], { type: 'application/pdf' });
			if (blob.size < 100) {
				const possibleError = await blob.text().catch(() => '');
				throw new Error(possibleError || 'Received invalid PDF payload from server');
			}
			const url = window.URL.createObjectURL(blob);
			const anchor = document.createElement('a');
			anchor.href = url;
			const slug = (value) => String(value || '')
				.toLowerCase()
				.replace(/[^a-z0-9]+/g, '-')
				.replace(/^-+|-+$/g, '')
				.slice(0, 40);
			const dateStamp = new Date().toISOString().slice(0, 10);
			const namePart = slug(form.clientName) || slug(form.clientId) || 'client';
			const bizPart = slug(form.businessType) || 'business';
			anchor.download = `wowrolla-prepilot-${namePart}-${bizPart}-${dateStamp}.pdf`;
			document.body.appendChild(anchor);
			anchor.click();
			anchor.remove();
			window.URL.revokeObjectURL(url);
			// Record pdf_ready journey stage.
			orchestratorApi.upsertJourneyStage({
				tenant_id: form.tenantId,
				client_id: form.clientId,
				stage: 'pdf_ready',
				metadata: { annual_ai_savings: projectedAnnualSavings },
			}).then(() => loadJourneyStages(form.tenantId, form.clientId)).catch(() => {});
			// Record PDF-generated session stat
			if (isAuthenticated) {
				const completedRuns = (state.simulationRuns || []).filter((r) => r.status === 'completed').length;
				consultingApi.saveSessionStats({
					business_type: form.businessType || '',
					simulation_runs: state.simulationRuns || [],
					completed_count: completedRuns,
					total_count: (state.simulationRuns || []).length,
					pdf_generated: true,
					annual_ai_savings: projectedAnnualSavings,
					payback_months: Number(readinessRoi.payback_months) || 0,
				}).catch(() => {});
			}
		} catch (error) {
			if (error?.response?.status === 404) {
				try {
					const printWindow = window.open('', '_blank', 'width=900,height=700');
					if (!printWindow) {
						throw new Error('Unable to open print window');
					}
					const html = buildPrintableRoiHtml({
						form,
						readinessProfile: state.readinessProfile,
						readinessRoi,
						profileDetail: state.profileDetail,
						simulationRuns: state.simulationRuns,
					});
					printWindow.document.open();
					printWindow.document.write(html);
					printWindow.document.close();
					printWindow.focus();
					printWindow.print();
					setState((prev) => ({ ...prev, error: '' }));
					return;
				} catch {
					// continue to default error
				}
			}
			const message = error?.response?.data?.detail || error?.message || 'Unable to generate PDF right now.';
			setState((prev) => ({ ...prev, error: message }));
		} finally {
			setState((prev) => ({ ...prev, pdfLoading: false }));
		}
	};

	return (
		<>
			<div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 prime-aurora-bg" style={{ minHeight: '100vh' }}>
			<Helmet>
				<title>My AI Flow | Wowrolla</title>
			</Helmet>

			<div className="max-w-5xl mx-auto space-y-6">
				<section className="prime-glow-card rounded-2xl p-6">
					<div className="mb-4">
						<Link
							to="/consulting#start"
							className="inline-flex items-center gap-2 px-4 py-2 text-xs font-mono font-bold uppercase tracking-widest border border-[#1c1c1c] text-[#FFE800] hover:text-white"
						>
							{'<- Back to Discovery Call'}
						</Link>
					</div>
					<p className="text-xs uppercase tracking-widest mb-2" style={{ color: 'var(--text-muted)' }}>Client Workspace</p>
					<h1 className="text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>My AI Flow</h1>
					<p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
						Run a guided simulation of your AI workflow, review expected outcomes, and prepare your ROI package.
					</p>
				</section>

				{!readinessComplete && (
					<section className="prime-glow-card rounded-2xl p-6" style={{ border: '1px solid rgba(255,232,0,0.3)' }}>
						<h2 className="text-lg font-semibold" style={{ color: '#FFE800' }}>First step: complete Readiness Studio</h2>
						<p className="mt-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
							Before running My AI Flow, complete your business profile in Readiness Studio. We automatically carry your team, tools, workflow priorities, and ROI baseline into this simulation.
						</p>
						<Link to="/consulting#roi" className="inline-flex mt-4 px-4 py-2 text-sm" style={{ border: '1px solid #FFE800', color: '#FFE800' }}>
							Open Readiness Studio
						</Link>
					</section>
				)}

				{readinessComplete && (
					<section className="prime-glow-card rounded-2xl p-6">
						<div className="mb-4 p-4 rounded-lg" style={{ background: 'rgba(255,232,0,0.06)', border: '1px solid rgba(255,232,0,0.3)' }}>
							<p className="text-xs uppercase tracking-wider" style={{ color: '#FFE800' }}>Guided flow</p>
							<p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
								Step 1 completed: Readiness Studio. Step 2: run simulation to execute all connected recipe workflows for your business type and generate a call-ready ROI PDF.
							</p>
						</div>

						{/* Journey Progress Stepper */}
						{(() => {
							const JOURNEY_STEPS = [
								{ key: 'readiness_audit', label: 'Readiness Studio', altKey: 'tool_picking' },
								{ key: 'simulation_started', label: 'Simulation Started' },
								{ key: 'pilot_completed', label: 'Pilot Completed' },
								{ key: 'pdf_ready', label: 'PDF Ready' },
							];
							const keys = state.journeyStageKeys || [];
							const activeIdx = JOURNEY_STEPS.reduce((last, step, idx) => {
								const hit = keys.includes(step.key) || (step.altKey && keys.includes(step.altKey));
								return hit ? idx : last;
							}, -1);
							if (keys.length === 0 && !state.simulationRuns?.length) return null;
							return (
								<div className="mb-5 flex items-center gap-0 overflow-x-auto">
									{JOURNEY_STEPS.map((step, idx) => {
										const done = keys.includes(step.key) || (step.altKey && keys.includes(step.altKey));
										const isActive = idx === activeIdx + 1 && !done;
										return (
											<React.Fragment key={step.key}>
												<div className="flex flex-col items-center min-w-[80px]">
													<div
														className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold border"
														style={{
															background: done ? 'rgba(57,255,20,0.15)' : isActive ? 'rgba(255,232,0,0.12)' : 'transparent',
															borderColor: done ? '#39FF14' : isActive ? '#FFE800' : '#2a2a2a',
															color: done ? '#39FF14' : isActive ? '#FFE800' : '#555',
														}}
													>
														{done ? '✓' : idx + 1}
													</div>
													<p className="text-center mt-1" style={{ fontSize: '9px', color: done ? '#39FF14' : isActive ? '#FFE800' : '#555', letterSpacing: '0.04em' }}>
														{step.label}
													</p>
												</div>
												{idx < JOURNEY_STEPS.length - 1 && (
													<div className="flex-1 h-px mb-4" style={{ background: done ? '#39FF14' : '#1a1a1a', minWidth: '24px' }} />
												)}
											</React.Fragment>
										);
									})}
								</div>
							);
						})()}

						<div className="grid md:grid-cols-3 gap-4 mb-3">
							<div className="p-4 rounded-lg" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
								<p className="text-xs uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Business Type</p>
								<p className="text-sm mt-2" style={{ color: 'var(--text-primary)' }}>{form.businessType}</p>
							</div>
							<div className="p-4 rounded-lg" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
								<p className="text-xs uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Projected Annual AI Savings</p>
								<p className="text-sm mt-2" style={{ color: 'var(--text-primary)' }}>{toMoney(projectedAnnualSavings)}</p>
							</div>
							<div className="p-4 rounded-lg" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
								<p className="text-xs uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Projected Payback</p>
								<p className="text-sm mt-2" style={{ color: 'var(--text-primary)' }}>{Number(readinessRoi.payback_months || 0).toFixed(1)} months</p>
							</div>
						</div>

						<div className="mb-4 p-3 rounded-lg" style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.35)' }}>
							<p className="text-xs uppercase tracking-wider" style={{ color: '#22c55e' }}>AI Engine In Use</p>
							<p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
								{state.aiRuntimeLabel
									? `${state.aiRuntimeLabel.provider || 'AI'} ${state.aiRuntimeLabel.model ? `- ${state.aiRuntimeLabel.model}` : ''}`
									: 'Loading runtime label...'}
							</p>
						</div>

						<div className="grid md:grid-cols-2 gap-4">
							<div className="space-y-4">
								<label className="block text-xs uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
									Tenant ID
									<input
										value={form.tenantId}
										onChange={(event) => updateField('tenantId', event.target.value)}
										className="mt-2 w-full rounded-xl bg-black/20 border border-white/10 px-4 py-3 text-sm"
									/>
								</label>
								<label className="block text-xs uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
									Client ID
									<input
										value={form.clientId}
										onChange={(event) => updateField('clientId', event.target.value)}
										className="mt-2 w-full rounded-xl bg-black/20 border border-white/10 px-4 py-3 text-sm"
									/>
								</label>
								<label className="block text-xs uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
									Client Name
									<input
										value={form.clientName}
										onChange={(event) => updateField('clientName', event.target.value)}
										className="mt-2 w-full rounded-xl bg-black/20 border border-white/10 px-4 py-3 text-sm"
									/>
								</label>
								<label className="block text-xs uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
									Contact Email
									<input
										value={form.contactEmail}
										onChange={(event) => updateField('contactEmail', event.target.value)}
										className="mt-2 w-full rounded-xl bg-black/20 border border-white/10 px-4 py-3 text-sm"
									/>
								</label>
							</div>
							<div className="space-y-4">
								<label className="block text-xs uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
									Business Type
									<select
										value={form.businessType}
										onChange={(event) => { updateField('businessType', event.target.value); }}
										className="mt-2 w-full rounded-xl bg-black/20 border border-white/10 px-4 py-3 text-sm"
									>
										<option value="">Select business type</option>
										{state.businessTypes.length > 0
											? state.businessTypes.map((type) => (
												<option key={type.business_type} value={type.business_type}>
													{type.label || type.business_type}
												</option>
											))
											: <option value="">{form.businessType || 'Select business type'}</option>}
									</select>
								</label>
								<label className="block text-xs uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
									Recipe Key
									<select
										value={form.recipeKey}
										onChange={(event) => updateField('recipeKey', event.target.value)}
										className="mt-2 w-full rounded-xl bg-black/20 border border-white/10 px-4 py-3 text-sm"
									>
										{recipeOptions.map((recipe) => (
											<option key={recipe} value={recipe}>{recipe}</option>
										))}
									</select>
								</label>
								<label className="block text-xs uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
									Region
									<input
										value={form.region}
										onChange={(event) => updateField('region', event.target.value)}
										className="mt-2 w-full rounded-xl bg-black/20 border border-white/10 px-4 py-3 text-sm"
									/>
								</label>
							</div>
						</div>

						<label className="block text-xs uppercase tracking-wider mt-4" style={{ color: 'var(--text-muted)' }}>
							Objective
							<input
								value={form.objective}
								onChange={(event) => updateField('objective', event.target.value)}
								className="mt-2 w-full rounded-xl bg-black/20 border border-white/10 px-4 py-3 text-sm"
							/>
						</label>

						<label className="block text-xs uppercase tracking-wider mt-4" style={{ color: 'var(--text-muted)' }}>
							Run Payload (JSON)
							<textarea
								value={form.runPayload}
								onChange={(event) => updateField('runPayload', event.target.value)}
								rows={12}
								className="mt-2 w-full rounded-xl bg-black/20 border border-white/10 px-4 py-3 text-sm font-mono"
							/>
						</label>

				<div className="mb-4 p-4 rounded-lg" style={{ background: 'rgba(255,232,0,0.06)', border: '1px solid rgba(255,232,0,0.3)' }}>
					<p className="text-xs uppercase tracking-wider" style={{ color: '#FFE800' }}>Next action</p>
					<p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{nextActionMessage}</p>
				</div>

				<div className="flex flex-wrap gap-3 mt-6">
					<button type="button" onClick={checkHealth} className="px-4 py-2 border border-white/20 rounded-xl text-sm">Check health</button>
					<button type="button" onClick={loadBusinessTypes} className="px-4 py-2 border border-white/20 rounded-xl text-sm" disabled={!state.health && state.businessTypes.length === 0 && !state.loading} title="Load business types after verifying orchestrator health.">Load business types</button>
					<button type="button" onClick={loadBusinessTypeDetail} className="px-4 py-2 border border-white/20 rounded-xl text-sm" disabled={!canLoadBusinessTypeDetail} title="Select a business type before loading its profile details.">Load profile detail</button>
					<button type="button" onClick={setupClientAndMission} className="px-4 py-2 border border-white/20 rounded-xl text-sm" disabled={!canSetupClient} title="Provide tenant ID, client ID, and a valid contact email to set up the client.">Setup client + mission</button>
					<button type="button" onClick={bootstrapClientByType} className="px-4 py-2 border border-white/20 rounded-xl text-sm" disabled={!canBootstrap} title="Bootstrap the client by business type once the client context is available.">Bootstrap client</button>
					<button type="button" onClick={createRun} className="px-4 py-2 border border-white/20 rounded-xl text-sm" disabled={!canCreateRun} title="Create a workflow run once tenant, client, and recipe are selected.">Create run</button>
					<button type="button" onClick={approveRun} className="px-4 py-2 border border-white/20 rounded-xl text-sm" disabled={!canApproveRun} title="Approve a run after it has been created.">Approve run</button>
					<button type="button" onClick={executeRun} className="px-4 py-2 border border-white/20 rounded-xl text-sm" disabled={!canExecuteRun} title="Execute the approved workflow run.">Execute run</button>
					<button type="button" onClick={refreshLogs} className="px-4 py-2 border border-white/20 rounded-xl text-sm" disabled={!canRefreshLogs} title="Refresh logs after tenant and client IDs are set.">Refresh logs</button>
					<button type="button" onClick={runFullWorkflow} className="px-4 py-2 rounded-xl text-sm font-semibold" style={{ background: '#FFE800', color: '#111' }} disabled={!canRunFullWorkflow} title="Run the full My AI Flow simulation once basic client context is valid.">Run simulation</button>
					<button type="button" onClick={downloadMyAiFlowPdf} disabled={!canDownloadPdf} className="px-4 py-2 rounded-xl text-sm font-semibold" style={{ background: '#39FF14', color: '#111' }}>
						Download PDF
					</button>
				</div>
				</section>
				)}
			</div>
		</div>
		</>
	);
};

export default OrchestratorLabPage;
